import React from 'react';
import { Link } from 'react-router-dom';
import { usePageSEO } from '../hooks/usePageSEO';
import { breadcrumbSchema } from '../utils/structuredData';
import { APPS_SUPPORT } from '../utils/appsData';
import { LifeBuoyIcon, ArrowRightIcon, MailIcon, MessageSquareIcon } from 'lucide-react';
export function SupportPage() {
  usePageSEO({
    title: 'Support Center | Design Flow Digitals',
    description: 'Get help, documentation, and support for Design Flow Digitals Shopify apps including SEO Suite, AI Photoshoot, and FlowBoost.',
    canonical: '/support',
    keywords: 'Shopify app support, Design Flow Digitals support, help center, app documentation',
    jsonLd: breadcrumbSchema([{
      name: 'Home',
      path: '/'
    }, {
      name: 'Support',
      path: '/support'
    }])
  });
  return <div className="w-full pt-32 pb-20" data-id="element-1606">
      {/* Hero */}
      <section className="px-6 md:px-12 max-w-5xl mx-auto text-center mb-20" data-id="element-1607">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-8 border-white/10" data-id="element-1608">
          <LifeBuoyIcon className="w-4 h-4 text-gray-400" data-id="element-1609" />
          <span className="text-sm font-medium text-gray-300" data-id="element-1610">
            Support Center
          </span>
        </div>
        <h1 className="text-5xl md:text-7xl font-bold mb-6" data-id="element-1611">
          How can we help you?
        </h1>
        <p className="text-xl md:text-2xl text-gray-400 leading-relaxed max-w-3xl mx-auto" data-id="element-1612">
          Select an app below to view setup guides, troubleshooting steps, and
          documentation.
        </p>
      </section>

      {/* Quick Contact */}
      <section className="px-6 md:px-12 max-w-3xl mx-auto mb-20" data-id="element-1613">
        <div className="glass-card p-6 md:p-8 rounded-3xl border-white/10 flex flex-col sm:flex-row items-center justify-between gap-6 text-center sm:text-left" data-id="element-1614">
          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6" data-id="element-1615">
            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-white/5 flex items-center justify-center shrink-0" data-id="element-1616">
              <MailIcon className="w-6 h-6 sm:w-8 sm:h-8 text-white" data-id="element-1617" />
            </div>
            <div data-id="element-1618">
              <h3 className="text-lg sm:text-xl font-bold mb-1" data-id="element-1619">
                Direct Support
              </h3>
              <p className="text-gray-400 text-sm" data-id="element-1620">
                Email us at{' '}
                <a href="mailto:support@designflowdigitals.com" className="text-white hover:underline" data-id="element-1621">
                  support@designflowdigitals.com
                </a>
              </p>
              <p className="text-gray-500 text-xs mt-1" data-id="element-1622">
                We typically reply within 24 hours.
              </p>
            </div>
          </div>
          <Link to="/contact" className="w-full sm:w-auto shrink-0 inline-flex justify-center items-center gap-2 bg-white text-black px-6 py-3 rounded-full font-semibold hover:bg-gray-200 transition-colors" data-id="element-1623">
            Open a Ticket
          </Link>
        </div>
      </section>

      {/* App Grid */}
      <section className="px-6 md:px-12 max-w-4xl mx-auto" data-id="element-1624">
        <h2 className="text-2xl font-bold mb-8 text-center" data-id="element-1625">
          App Documentation
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8" data-id="element-1626">
          {APPS_SUPPORT.map(app => {
          const Icon = app.icon;
          return <Link key={app.slug} to={`/support/${app.slug}`} className="glass-card p-8 rounded-3xl border-white/5 hover:border-white/20 transition-all duration-300 group hover:-translate-y-1" data-id="element-1627">
                <div className={`w-14 h-14 rounded-2xl mb-6 flex items-center justify-center ${app.accentBg} border ${app.accentBorder} group-hover:scale-110 transition-transform duration-300`} data-id="element-1628">
                  <Icon className={`w-7 h-7 ${app.accentColor}`} data-id="element-1629" />
                </div>
                <h3 className="text-2xl font-bold mb-2 text-white group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-white group-hover:to-gray-400 transition-all" data-id="element-1630">
                  {app.name}
                </h3>
                <p className="text-gray-400 mb-8 line-clamp-2" data-id="element-1631">
                  {app.description}
                </p>
                <div className={`inline-flex items-center gap-2 text-sm font-medium ${app.accentColor}`} data-id="element-1632">
                  View Documentation{' '}
                  <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-1633" />
                </div>
              </Link>;
        })}
        </div>
      </section>

      {/* General FAQ / Bottom CTA */}
      <section className="px-6 md:px-12 max-w-4xl mx-auto mt-32 text-center" data-id="element-1634">
        <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-6" data-id="element-1635">
          <MessageSquareIcon className="w-8 h-8 text-gray-400" data-id="element-1636" />
        </div>
        <h2 className="text-3xl font-bold mb-4" data-id="element-1637">Have a general inquiry?</h2>
        <p className="text-gray-400 mb-8 max-w-2xl mx-auto" data-id="element-1638">
          Whether you have a question about billing, custom development, or
          partnership opportunities, our team is ready to assist.
        </p>
        <Link to="/contact" className="inline-flex items-center gap-2 bg-gradient-gold text-black px-8 py-4 rounded-full font-bold gold-glow-hover transition-all hover:scale-105" data-id="element-1639">
          Contact Us <ArrowRightIcon className="w-5 h-5" data-id="element-1640" />
        </Link>
      </section>
    </div>;
}